import { addDays, format, startOfDay } from "date-fns";
import { DATE_FORMATS, DISPLAY_CONSTANTS, EQuickFilterId, TIME_FORMATS } from "../constants";
import { isNullOrUndefined, isDefined } from "./common";
import { IDateOfBirth, IUpdateDateOfBirth } from "../interfaces";

/**
 * Common date utility functions
 */

/**
 * Gets the current year and month
 * @returns Object with year and month (month is 1-indexed, 1-12)
 */
export const getCurrentYearMonth = (): { year: number; month: number } => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1, // JavaScript months are 0-indexed
  };
};

/**
 * Extracts year and month from a timestamp or Date object
 * @param timestamp - Timestamp in milliseconds or Date object
 * @returns Object with year and month (month is 1-indexed, 1-12) or undefined if timestamp is null/undefined
 */
export const getYearMonthFromTimestamp = (
  timestamp?: number | Date | null
): { year: number; month: number } | undefined => {
  if (isNullOrUndefined(timestamp)) return undefined;
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // JavaScript months are 0-indexed
  };
};

/**
 * Creates a scheduled timestamp from date and time components
 * @param year - Year
 * @param month - Month (1-indexed)
 * @param day - Day
 * @param hour - Hour (24-hour format)
 * @param minute - Minute
 * @returns Timestamp in milliseconds
 */
export const createScheduledTimestamp = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number
): number => {
  return new Date(
    year,
    month - 1, // JavaScript months are 0-indexed
    day,
    hour,
    minute
  ).getTime();
};

/**
 * Formats a date value (timestamp) to display date format
 * @param dateValue - Date value as timestamp (number)
 * @returns Formatted date string or "N/A" if dateValue is falsy
 */
export const formatDateOnly = (dateValue?: number): string => {
  if (isNullOrUndefined(dateValue)) return DISPLAY_CONSTANTS.NOT_AVAILABLE;
  return format(new Date(dateValue), DATE_FORMATS.DISPLAY_DATE);
};

/**
 * Formats a date value (timestamp) to display date and time
 * @param dateValue - Date value as timestamp (number)
 * @returns Formatted date and time string or "-" if dateValue is falsy
 */
export const formatDateAndTime = (dateValue?: number): string => {
  if (isNullOrUndefined(dateValue)) return "-";
  return format(new Date(dateValue), DATE_FORMATS.DISPLAY_DATE + ", " + TIME_FORMATS.FULL_TIME);
};

/**
 * Gets today's date at the start of the day (00:00:00.000)
 * @returns Date object set to today at midnight
 */
export const getTodayStart = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * Gets tomorrow's date at the start of the day (00:00:00.000), local calendar.
 */
export const getTomorrowStart = (): Date => {
  return startOfDay(addDays(new Date(), 1));
};

/**
 * Gets the start of day for a given date (00:00:00.000)
 * @param date - Date to get start of day for
 * @returns Date object set to start of the given day
 */
export const getStartOfDay = (date: Date): Date => {
  return startOfDay(date);
};

/**
 * Gets the end of day for a given date (23:59:59.999)
 * @param date - Date to get end of day for
 * @returns Date object set to end of the given day
 */
export const getEndOfDay = (date: Date): Date => {
  const eod = new Date(date);
  eod.setHours(23, 59, 59, 999);
  return eod;
};

/**
 * Calculate date range timestamps for "yesterday", "today", or "tomorrow"
 * Core logic extracted from getDateRangeFromQuickFilterGeneric for reuse
 * @param filterId - Filter ID enum value: YESTERDAY, TODAY, or TOMORROW
 * @returns Object with start and end timestamps
 */
export const getDateRangeTimestamps = (
  filterId: EQuickFilterId.YESTERDAY | EQuickFilterId.TODAY | EQuickFilterId.TOMORROW
): { start: number; end: number } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (filterId === EQuickFilterId.TODAY) {
    const todayStart = today.getTime();
    const todayEnd = new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1).getTime();
    return { start: todayStart, end: todayEnd };
  }

  if (filterId === EQuickFilterId.YESTERDAY) {
    const yesterdayStart = new Date(today.getTime() - 24 * 60 * 60 * 1000).getTime();
    const yesterdayEnd = today.getTime() - 1;
    return { start: yesterdayStart, end: yesterdayEnd };
  }

  // TOMORROW
  const tomorrowStart = new Date(today.getTime() + 24 * 60 * 60 * 1000).getTime();
  const tomorrowEnd = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000 - 1).getTime();
  return { start: tomorrowStart, end: tomorrowEnd };
};

/**
 * Gets the start timestamp of a month (first day at 00:00:00.000)
 * @param year - Year (e.g., 2024)
 * @param month - Month (1-indexed, 1-12)
 * @returns Timestamp in milliseconds for the start of the month
 */
export const getMonthStartTimestamp = (year: number, month: number): number => {
  return new Date(year, month - 1, 1).getTime();
};

/**
 * Gets the end timestamp of a month (last day at 23:59:59.999)
 * @param year - Year (e.g., 2024)
 * @param month - Month (1-indexed, 1-12)
 * @returns Timestamp in milliseconds for the end of the month
 */
export const getMonthEndTimestamp = (year: number, month: number): number => {
  return new Date(year, month, 0, 23, 59, 59, 999).getTime();
};

export const formatFullMonthYear = (date: Date): string => {
  return format(date, DATE_FORMATS.FULL_MONTH_YEAR);
}
/**
 * Parse date string in ISO format (YYYY-MM-DD) into year, month, day
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Object with year, month, day or null if invalid
 */
export const parseDateString = (dateString: string): { year: number; month: number; day: number } | null => {
  if (!isDefined(dateString) || typeof dateString !== "string") return null;

  const dateParts = dateString.split('-');
  if (dateParts.length !== 3) return null;

  const yearStr = dateParts[0];
  const monthStr = dateParts[1];
  const dayStr = dateParts[2];

  if (!isDefined(yearStr) || !isDefined(monthStr) || !isDefined(dayStr)) return null;

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  return { year, month, day };
};

/**
 * Parse time string in HH:MM format into hour, minute
 * @param timeString - Time string in HH:MM format
 * @returns Object with hour, minute or null if invalid
 */
export const parseTimeString = (timeString: string): { hour: number; minute: number } | null => {
  if (!isDefined(timeString) || typeof timeString !== "string") return null;

  const timeParts = timeString.split(':');
  if (timeParts.length < 2) return null;

  const hourStr = timeParts[0];
  const minuteStr = timeParts[1];

  if (!isDefined(hourStr) || !isDefined(minuteStr)) return null;

  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (isNaN(hour) || isNaN(minute)) return null;

  return { hour, minute };
};

/**
 * Create scheduled timestamp from date and time strings (HTML input format)
 * Uses parseDateString and parseTimeString utility functions
 * @param dateString - Date string in YYYY-MM-DD format (HTML date input)
 * @param timeString - Time string in HH:MM format (HTML time input)
 * @returns Timestamp in milliseconds or null if invalid
 */
export const createScheduledTimestampFromStrings = (
  dateString: string,
  timeString: string
): number | null => {
  const dateParts = parseDateString(dateString);
  const timeParts = parseTimeString(timeString);

  if (!isDefined(dateParts) || !isDefined(timeParts)) return null;

  return createScheduledTimestamp(
    dateParts.year,
    dateParts.month,
    dateParts.day,
    timeParts.hour,
    timeParts.minute
  );
};

/**
 * Checks if two dates/timestamps are on the same day
 * @param date1 - First date (Date object or timestamp in milliseconds)
 * @param date2 - Second date (Date object or timestamp in milliseconds)
 * @returns true if both dates are on the same day, false otherwise
 */
export const isSameDay = (
  date1: Date | number,
  date2: Date | number
): boolean => {
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

/**
 * Formats a date range with conditional time display based on whether dates are on the same day
 * If both dates are on the same day: returns date in primary, time range in secondary
 * If dates are different: returns date range in primary, no secondary
 * @param startsAt - Start timestamp in milliseconds
 * @param endsAt - End timestamp in milliseconds
 * @param formatDateTimeFn - Function to format date and time (returns { date: string; time: string })
 * @param formatDateOnlyFn - Function to format date only (returns string)
 * @returns Object with primary (required) and secondary (optional) display strings
 */
export const formatDateRangeWithConditionalTime = (
  startsAt: number,
  endsAt: number,
  formatDateTimeFn: (timestamp: number) => { date: string; time: string } | null,
  formatDateOnlyFn: (timestamp: number) => string
): { primary: string; secondary?: string } => {
  const startDate = new Date(startsAt);
  const endDate = new Date(endsAt);

  const sameDay = isSameDay(startDate, endDate);

  if (sameDay) {
    // Same day: show date in primary, time range in secondary
    const formattedStart = formatDateTimeFn(startsAt);
    const formattedEnd = formatDateTimeFn(endsAt);

    if (isDefined(formattedStart) && isDefined(formattedEnd)) {
      return {
        primary: formattedStart.date,
        secondary: `${formattedStart.time} - ${formattedEnd.time}`,
      };
    }
  }

  // Different days: show only dates in primary, no secondary
  const startDateStr = formatDateOnlyFn(startsAt);
  const endDateStr = formatDateOnlyFn(endsAt);

  return {
    primary: `${startDateStr} - ${endDateStr}`,
  };
};

/**
 * Convert dateOfBirth object to Date
 * @param dob - Date of birth object with year, month, date
 * @returns Date object or undefined if dob is invalid
 */
export const convertDateOfBirthToDate = (dob?: IDateOfBirth | null): Date | undefined => {
  if (!dob || !isDefined(dob.year) || !isDefined(dob.month) || !isDefined(dob.date)) {
    return undefined;
  }
  return new Date(dob.year, dob.month - 1, dob.date);
};

/**
 * Convert Date to dateOfBirth object
 * @param date - Date object
 * @returns Date of birth object with year, month, date or null if date is invalid
 */
export const convertDateToDateOfBirth = (date: Date | null | undefined): IDateOfBirth | null | undefined => {
  if (!isDefined(date)) {
    return null;
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    date: date.getDate(),
  };
};

/**
 * Convert Date to dateOfBirth object
 * @param date - Date object
 * @returns Date of birth object with year, month, date or null if date is invalid
 */
export const convertDateToUpdateDateOfBirth = (date: Date | null | undefined): IUpdateDateOfBirth | null | undefined => {
  if (!isDefined(date)) {
    return null;
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};
